import { Suspense, useEffect, useMemo, useRef, useState } from "react";
import { Canvas, useFrame, useLoader } from "@react-three/fiber";
import { Float, OrbitControls, Text } from "@react-three/drei";
import * as THREE from "three";
import { Crown, Loader2, Lock, Play, Square, Waves, Filter, Rewind } from "lucide-react";
import { API } from "@/assets";
import { useSiteAuth } from "@/components/GoogleAuth";
import { MedusaEngine } from "@/components/medusa/engine";
import { SectionShell } from "@/components/medusa/SectionShell";
import { grantXP } from "@/components/medusa/player";

/* ---------- reactive lighting + VU meters read the live engine level ---------- */
function ReactiveLight({ engine, color, position, base = 0.6, range = 2.2 }) {
  const ref = useRef();
  useFrame(() => {
    if (!ref.current) return;
    const level = engine.current?.level?.() || 0;
    ref.current.intensity = base + level * range * 9;
  });
  return <pointLight ref={ref} position={position} color={color} intensity={base} distance={14} decay={2} />;
}

function VuBar({ engine, position, color = "#00FF66", idx = 0 }) {
  const ref = useRef();
  useFrame(({ clock }) => {
    if (!ref.current) return;
    const level = engine.current?.level?.() || 0;
    const jitter = 0.35 + 0.65 * Math.abs(Math.sin(clock.elapsedTime * (3 + idx * 0.7) + idx));
    const h = 0.08 + level * 10 * jitter * 0.16;
    ref.current.scale.y = h;
    ref.current.position.y = position[1] + h / 2;
  });
  return (
    <mesh ref={ref} position={position}>
      <boxGeometry args={[0.09, 1, 0.06]} />
      <meshStandardMaterial color={color} emissive={color} emissiveIntensity={2.2} toneMapped={false} />
    </mesh>
  );
}

/* ---------- the console: angled desk, faders, clickable ---------- */
function MixingConsole({ onFocus }) {
  const [hover, setHover] = useState(false);
  return (
    <group position={[0, 0.78, 0]}
      onClick={(e) => { e.stopPropagation(); onFocus(); }}
      onPointerOver={() => setHover(true)}
      onPointerOut={() => setHover(false)}>
      <mesh castShadow>
        <boxGeometry args={[4.6, 0.18, 1.7]} />
        <meshStandardMaterial color="#17171d" roughness={0.35} metalness={0.75} emissive={hover ? "#3b0764" : "#000000"} emissiveIntensity={hover ? 0.5 : 0} />
      </mesh>
      {/* faders */}
      {Array.from({ length: 8 }).map((_, i) => (
        <group key={i} position={[-1.75 + i * 0.5, 0.1, 0]}>
          <mesh position={[0, 0, 0]}>
            <boxGeometry args={[0.05, 0.02, 1.2]} />
            <meshStandardMaterial color="#0a0a0e" roughness={0.6} />
          </mesh>
          <mesh position={[0, 0.04, -0.5 + ((i * 37) % 100) / 100]} castShadow>
            <boxGeometry args={[0.14, 0.06, 0.16]} />
            <meshStandardMaterial color="#e4e4e7" roughness={0.3} metalness={0.6} />
          </mesh>
        </group>
      ))}
      {/* master strip */}
      <mesh position={[2.05, 0.1, 0]}>
        <boxGeometry args={[0.35, 0.03, 1.5]} />
        <meshStandardMaterial color="#A855F7" emissive="#7c3aed" emissiveIntensity={0.8} roughness={0.4} />
      </mesh>
    </group>
  );
}

function MonitorSpeaker({ position, rotation = [0, 0, 0] }) {
  return (
    <group position={position} rotation={rotation}>
      <mesh castShadow>
        <boxGeometry args={[0.55, 0.85, 0.5]} />
        <meshStandardMaterial color="#101014" roughness={0.5} metalness={0.4} />
      </mesh>
      <mesh position={[0, 0.18, 0.26]}>
        <circleGeometry args={[0.16, 24]} />
        <meshStandardMaterial color="#1f1f26" roughness={0.4} />
      </mesh>
      <mesh position={[0, -0.2, 0.26]}>
        <circleGeometry args={[0.1, 24]} />
        <meshStandardMaterial color="#26262e" roughness={0.4} />
      </mesh>
    </group>
  );
}

function StudioScene({ engine, onConsoleFocus }) {
  const logoTex = useLoader(THREE.TextureLoader, "/medusa-logo.webp");
  const logo = useMemo(() => { logoTex.colorSpace = THREE.SRGBColorSpace; return logoTex; }, [logoTex]);

  return (
    <>
      <color attach="background" args={["#050507"]} />
      <fog attach="fog" args={["#050507", 9, 22]} />
      <ambientLight intensity={0.12} />
      <ReactiveLight engine={engine} color="#A855F7" position={[-3.5, 3.2, 1]} />
      <ReactiveLight engine={engine} color="#00F0FF" position={[3.5, 3.2, 1]} />
      <ReactiveLight engine={engine} color="#FFB800" position={[0, 2.6, -3]} base={0.35} range={1.4} />
      <spotLight position={[0, 5, 3]} angle={0.6} penumbra={0.8} intensity={0.5} color="#ffffff" castShadow shadow-mapSize={[512, 512]} />

      {/* floor + back wall */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0, 0]} receiveShadow>
        <planeGeometry args={[26, 26]} />
        <meshStandardMaterial color="#0b0b10" roughness={0.25} metalness={0.65} />
      </mesh>
      <mesh position={[0, 3, -5.2]}>
        <planeGeometry args={[26, 9]} />
        <meshStandardMaterial color="#0d0d13" roughness={0.85} />
      </mesh>

      {/* medusa emblem on the wall, faintly lit */}
      <Float speed={1.2} rotationIntensity={0.05} floatIntensity={0.25}>
        <mesh position={[0, 3.1, -5.1]}>
          <planeGeometry args={[2.6, 2.6]} />
          <meshStandardMaterial map={logo} transparent emissive="#A855F7" emissiveMap={logo} emissiveIntensity={0.5} roughness={0.6} />
        </mesh>
      </Float>
      <Text position={[0, 1.45, -5.05]} fontSize={0.22} color="#8b8b96" anchorX="center"
        font="https://fonts.gstatic.com/s/spacegrotesk/v16/V8mQoQDjQSkFtoMM3T6r8E7mFpbQ-q5.woff2">
        COLD-BLOODED SOUNDSCAPES
      </Text>

      {/* console desk + gear */}
      <MixingConsole onFocus={onConsoleFocus} />
      <mesh position={[0, 0.35, 0]} castShadow>
        <boxGeometry args={[5, 0.7, 2]} />
        <meshStandardMaterial color="#0f0f14" roughness={0.4} metalness={0.5} />
      </mesh>
      <MonitorSpeaker position={[-2.8, 1.2, -0.6]} rotation={[0, 0.35, 0]} />
      <MonitorSpeaker position={[2.8, 1.2, -0.6]} rotation={[0, -0.35, 0]} />

      {/* outboard rack with live VU meters */}
      <group position={[-4.2, 1.1, -1.2]} rotation={[0, 0.5, 0]}>
        <mesh castShadow>
          <boxGeometry args={[1, 2.2, 0.7]} />
          <meshStandardMaterial color="#121218" roughness={0.4} metalness={0.7} />
        </mesh>
        {[0.75, 0.35, -0.05, -0.45].map((y, r) => (
          <group key={r} position={[0, y, 0.36]}>
            <mesh>
              <boxGeometry args={[0.9, 0.3, 0.03]} />
              <meshStandardMaterial color="#17171d" roughness={0.5} />
            </mesh>
            {Array.from({ length: 5 }).map((_, i) => (
              <VuBar key={i} engine={engine} idx={r * 5 + i} position={[-0.3 + i * 0.15, -0.13, 0.03]} color={r % 2 ? "#00F0FF" : "#00FF66"} />
            ))}
          </group>
        ))}
      </group>

      <OrbitControls
        enablePan={false}
        minDistance={2.6} maxDistance={9}
        minPolarAngle={0.9} maxPolarAngle={1.52}
        target={[0, 1.1, 0]}
      />
    </>
  );
}

/* ---------- page ---------- */
export default function Studio3DPage() {
  const { user } = useSiteAuth();
  const engine = useRef(null);
  if (!engine.current && typeof window !== "undefined") engine.current = new MedusaEngine();

  const [member, setMember] = useState(undefined); // undefined = checking
  const [playing, setPlaying] = useState(false);
  const [focus, setFocus] = useState(false);
  const [fx, setFx] = useState({ reverb: false, lowpass: false });

  useEffect(() => {
    if (!user) { setMember(false); return; }
    fetch(`${API}/medusa/status`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : { member: false }))
      .then((d) => setMember(!!d.member))
      .catch(() => setMember(false));
  }, [user]);

  useEffect(() => () => engine.current?.stopBeat?.(), []);

  const toggleBeat = async () => {
    if (playing) { engine.current.stopBeat(); setPlaying(false); return; }
    await engine.current.startStems();
    setPlaying(true);
    grantXP("studio", 20, "Free Play session started");
  };

  const toggleFx = (name) => {
    const on = !fx[name];
    setFx((f) => ({ ...f, [name]: on }));
    if (name === "reverb") engine.current.toggleReverb(on);
    if (name === "lowpass") engine.current.toggleLowpass(on);
  };

  return (
    <SectionShell
      kicker="Section VI · Members Only"
      title="Free Play Studio"
      blurb="The room's full 3D studio. Start the beat, walk the console, and shape the mix on the gear — the room reacts to your sound."
      color="#FFB800"
      testId="studio3d-page"
    >
      <div className="relative rounded-2xl overflow-hidden border border-yellow-500/25 bg-black" data-testid="studio3d-canvas-wrap">
        <div className="h-[62vh] min-h-[420px]">
          <Canvas shadows dpr={[1, 1.5]} camera={{ position: [0, 2.4, 5.6], fov: 50 }} gl={{ antialias: true, powerPreference: "high-performance" }}>
            <Suspense fallback={null}>
              <StudioScene engine={engine} onConsoleFocus={() => setFocus((f) => !f)} />
            </Suspense>
          </Canvas>
        </div>

        {/* transport + console focus panel */}
        <div className="absolute bottom-4 left-4 flex flex-wrap gap-2.5" data-testid="studio3d-transport">
          <button onClick={toggleBeat}
            className="inline-flex items-center gap-2 bg-gradient-to-r from-yellow-400 to-amber-500 text-black font-bold text-xs px-6 py-3 rounded-full active:scale-95 transition-all"
            data-testid="studio3d-play">
            {playing ? <Square size={13} /> : <Play size={13} />} {playing ? "Stop the Beat" : "Play the Beat"}
          </button>
          <button onClick={() => setFocus((f) => !f)}
            className="inline-flex items-center gap-2 border border-white/20 text-ice/70 font-bold text-xs px-5 py-3 rounded-full hover:border-yellow-400/60 hover:text-yellow-300 transition-all"
            data-testid="studio3d-console-toggle">
            Console Controls
          </button>
        </div>

        {focus && (
          <div className="absolute top-4 right-4 w-64 rounded-2xl border border-yellow-500/30 bg-black/80 backdrop-blur p-4" data-testid="studio3d-console">
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-yellow-300 mb-3">Master Console</p>
            <div className="grid grid-cols-3 gap-2">
              <button onClick={() => toggleFx("reverb")} aria-pressed={fx.reverb}
                className={`rounded-xl border p-3 text-center transition-all active:scale-95 ${fx.reverb ? "border-teal-400 bg-teal-500/10" : "border-[#26262c]"}`} data-testid="studio-fx-reverb">
                <Waves size={16} className={`mx-auto ${fx.reverb ? "text-teal-300" : "text-ice/35"}`} />
                <p className="mt-1 font-mono text-[8px] uppercase tracking-[0.12em] text-ice/50">Reverb</p>
              </button>
              <button onClick={() => engine.current.tapeStop()}
                className="rounded-xl border border-[#26262c] p-3 text-center transition-all active:scale-90 hover:border-yellow-500/50" data-testid="studio-fx-tapestop">
                <Rewind size={16} className="mx-auto text-yellow-300/80" />
                <p className="mt-1 font-mono text-[8px] uppercase tracking-[0.12em] text-ice/50">Tape Stop</p>
              </button>
              <button onClick={() => toggleFx("lowpass")} aria-pressed={fx.lowpass}
                className={`rounded-xl border p-3 text-center transition-all active:scale-95 ${fx.lowpass ? "border-pink-400 bg-pink-500/10" : "border-[#26262c]"}`} data-testid="studio-fx-lowpass">
                <Filter size={16} className={`mx-auto ${fx.lowpass ? "text-pink-300" : "text-ice/35"}`} />
                <p className="mt-1 font-mono text-[8px] uppercase tracking-[0.12em] text-ice/50">Low-Pass</p>
              </button>
            </div>
            <p className="mt-3 text-[10px] text-ice/40">Click the console in the room to toggle this panel. Drag to orbit; scroll to zoom.</p>
          </div>
        )}

        {/* membership gate */}
        {member === false && (
          <div className="absolute inset-0 bg-black/85 backdrop-blur-sm flex flex-col items-center justify-center text-center px-6" data-testid="studio3d-lock">
            <Lock size={30} className="text-yellow-300 mb-4" />
            <p className="font-display text-white font-bold text-xl">The Studio is Vault Territory</p>
            <p className="mt-2 text-sm text-ice/55 max-w-sm">Free Play is a VIP section. Membership is $9.99/month after a 7-day free trial, or $99/year — it also unlocks the Vault drops.</p>
            <a href="/medusa#medusa-join"
              className="mt-5 inline-flex items-center gap-2 bg-gradient-to-r from-yellow-400 to-amber-500 text-black font-bold text-sm px-8 py-3.5 rounded-full active:scale-95 transition-all"
              data-testid="studio3d-join">
              <Crown size={15} /> Unlock with Membership
            </a>
          </div>
        )}
        {member === undefined && (
          <div className="absolute inset-0 bg-black/70 flex items-center justify-center">
            <Loader2 className="animate-spin text-yellow-300" size={26} />
          </div>
        )}
      </div>
    </SectionShell>
  );
}
